/**
 * Pure state machine for FlagsmithIdentityProvider.
 *
 * Keeping the state shape, reducer, and selectors separate from the React component
 * means they can be unit-tested without a React renderer, a jsdom environment, or the
 * Flagsmith SDK.  The provider imports from here and remains a thin effects wrapper.
 */

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

/**
 * Subset of the user profile sent to Flagsmith as identity traits.
 * Defined inline here (rather than derived from UserProfile) so this module
 * has zero external imports and can be loaded by node:test without path-alias
 * resolution.
 */
export type FlagsmithTraits = {
  email?: string;
  name?: string;
  role?: 'teacher' | 'user' | 'admin';
  schoolId?: string;
};

export type IdentityState = {
  /** True while a fetch (anonymous getFlags or identify) is in flight. */
  loading: boolean;
  /** Email of the last user whose identify() call *succeeded*. Advances only on success. */
  prevEmail?: string;
  /**
   * Email of the last user we *attempted* to identify, regardless of outcome.  Used by
   * shouldIdentify to avoid retrying the same email after a failure — separate from
   * prevEmail so that a switch to a different email still triggers a fresh attempt even
   * when identifyFailed is true.
   */
  attemptedEmail?: string;
  fetchedAnonymous: boolean;
  /** True after flagsmith.identify() rejects, cleared on the next attempt. */
  identifyFailed: boolean;
};

export const initialState: IdentityState = {
  loading: true,
  prevEmail: undefined,
  attemptedEmail: undefined,
  fetchedAnonymous: false,
  identifyFailed: false,
};

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

/**
 * Unified fetch lifecycle.  `anonymous: true` covers `flagsmith.getFlags()`;
 * `anonymous: false` covers `flagsmith.identify(email)`.  Modeling both as one
 * lifecycle lets state.loading be normalized rather than per-operation.
 */
export type IdentityAction =
  | { type: 'FETCH_STARTED'; anonymous: true }
  | { type: 'FETCH_STARTED'; anonymous: false; email: string }
  | { type: 'FETCH_SUCCEEDED'; anonymous: true }
  | { type: 'FETCH_SUCCEEDED'; anonymous: false; email: string }
  | { type: 'FETCH_FAILED'; anonymous: true }
  | { type: 'FETCH_FAILED'; anonymous: false }
  | { type: 'LOGOUT_COMPLETE' };

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export function reducer(state: IdentityState, action: IdentityAction): IdentityState {
  switch (action.type) {
    case 'FETCH_STARTED':
      if (action.anonymous) {
        return { ...state, loading: true };
      }
      return {
        ...state,
        loading: true,
        attemptedEmail: action.email,
        identifyFailed: false,
      };

    case 'FETCH_SUCCEEDED':
      if (action.anonymous) {
        return { ...state, loading: false, fetchedAnonymous: true };
      }
      return {
        ...state,
        loading: false,
        prevEmail: action.email,
        identifyFailed: false,
      };

    case 'FETCH_FAILED':
      if (action.anonymous) {
        // Even on failure, mark anonymous as attempted so we don't loop on it.
        return { ...state, loading: false, fetchedAnonymous: true };
      }
      return {
        ...state,
        loading: false,
        identifyFailed: true,
      };

    case 'LOGOUT_COMPLETE':
      // flagsmith.logout() internally re-fetches anonymous flags, so treat that as done.
      return {
        loading: false,
        prevEmail: undefined,
        attemptedEmail: undefined,
        fetchedAnonymous: true,
        identifyFailed: false,
      };
  }
}
